'use client';

import { useTranslations } from '@heidi/runtime/i18n';
import { cn } from '@heidi/utils';
import type { DetailLevel } from '../../../../learnBasicsTypes';

import { DETAIL_LEVELS } from './detail-levels';

interface DetailLevelSelectorProps {
  level: DetailLevel;
  onSelect: (level: DetailLevel) => void;
  /** Hide the helper description (e.g. in the compact preview popover). */
  hideDescription?: boolean;
  /**
   * Lock the control — used while the preview is still writing the note, since
   * there is no note to re-cut at a different verbosity yet.
   */
  disabled?: boolean;
}

/**
 * Presentational Low/Medium/High detail control rendered as a 3-stop slider
 * (track + fill + thumb) with clickable labels, plus a description. Shared by
 * the left-rail step control (Learn-the-basics step 1) and the preview popover
 * (`DetailLevelPanel`). The labels are real buttons so it stays keyboard- and
 * screen-reader-accessible; the track/fill/thumb are decorative.
 */
export function DetailLevelSelector({
  level,
  onSelect,
  hideDescription,
  disabled = false,
}: DetailLevelSelectorProps) {
  const { t } = useTranslations();
  const activeIndex = Math.max(
    0,
    DETAIL_LEVELS.findIndex((l) => l.value === level)
  );
  const active = DETAIL_LEVELS[activeIndex] ?? DETAIL_LEVELS[1]!;
  const pct = (activeIndex / (DETAIL_LEVELS.length - 1)) * 100;

  const lastIndex = DETAIL_LEVELS.length - 1;

  // The thumb travels inside the track rather than straddling its ends: at Low a
  // centre-anchored thumb hangs half its width past the left edge, which the
  // step column's scroll container clips into a half-circle. Inset the travel by
  // half the 16px thumb so the ends sit flush.
  const thumbCentre = `calc(${pct}% + ${8 - (pct / 100) * 16}px)`;

  return (
    <div
      className={cn(
        'flex flex-col gap-2.5 transition-opacity',
        disabled && 'pointer-events-none opacity-50'
      )}
    >
      <div className="pt-1">
        {/* Native range drives click + drag + keyboard; the track/fill/thumb
            below are the styled, decorative reflection of its value. */}
        <div className="relative flex h-4 items-center">
          <div className="relative h-1.5 w-full rounded-full bg-background-secondary">
            <div
              aria-hidden
              className="absolute left-0 top-0 h-full rounded-full bg-text-primary transition-[width] duration-150 ease-out"
              style={{ width: thumbCentre }}
            />
            <div
              aria-hidden
              className="absolute top-1/2 size-4 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-text-primary bg-surface shadow transition-[left] duration-150 ease-out"
              style={{ left: thumbCentre }}
            />
          </div>
          <input
            type="range"
            min={0}
            max={lastIndex}
            step={1}
            value={activeIndex}
            disabled={disabled}
            onChange={(e) => {
              const next = DETAIL_LEVELS[Number(e.target.value)];
              if (next) onSelect(next.value);
            }}
            aria-label={t('signup.detailLevelSliderLabel')}
            className={cn(
              'absolute inset-0 size-full opacity-0',
              disabled ? 'cursor-default' : 'cursor-pointer'
            )}
          />
        </div>
        <div className="mt-2.5 flex justify-between">
          {DETAIL_LEVELS.map((l) => (
            <button
              key={l.value}
              type="button"
              disabled={disabled}
              onClick={() => onSelect(l.value)}
              aria-pressed={level === l.value}
              className={cn(
                'text-xs font-medium transition-colors',
                level === l.value
                  ? 'text-text-primary'
                  : 'text-text-tertiary hover:text-text-secondary'
              )}
            >
              {t(l.labelKey)}
            </button>
          ))}
        </div>
      </div>
      {!hideDescription && (
        <p className="text-xs text-text-tertiary">{t(active.descKey)}</p>
      )}
    </div>
  );
}
